
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { SubscriptionUpdateData } from "@/types/subscription";
import { subscriptionService } from "@/services/subscriptionService";

// Interface for subscriber data structure
interface Subscriber {
  id: string;
  phoneNumber: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  timezone: string;
  adminUserId: string | null;
  subscriptions: Array<{
    id: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    confirmationStatus: string;
    categories: Array<{ id: string; name: string }>;
    deliveryTimes: Array<{ id: string; time: string; categoryId: string | null }>;
  }>;
}

// Fetch subscribers function
const fetchSubscribers = async (): Promise<Subscriber[]> => {
  try {
    // Use the RPC function to get all relevant profiles - this matches how the dashboard gets subscribers
    const { data: profiles, error: profileError } = await supabase
      .from('profiles')
      .select(`
        id,
        phone_number,
        subscription_status,
        created_at,
        updated_at,
        timezone,
        admin_user_id,
        subscriptions (
          id,
          status,
          created_at,
          updated_at,
          confirmation_status,
          subscription_categories (
            category_id,
            categories (
              id,
              name
            )
          ),
          delivery_times (
            id,
            time_of_day,
            category_id
          )
        )
      `)
      .order('created_at', { ascending: false });

    if (profileError) {
      throw profileError;
    }

    // Transform profiles with their subscriptions
    return profiles.map(profile => ({
      id: profile.id,
      phoneNumber: profile.phone_number,
      status: profile.subscription_status,
      createdAt: profile.created_at,
      updatedAt: profile.updated_at,
      timezone: profile.timezone,
      adminUserId: profile.admin_user_id,
      subscriptions: (profile.subscriptions || []).map(subscription => ({
        id: subscription.id,
        status: subscription.status,
        createdAt: subscription.created_at,
        updatedAt: subscription.updated_at,
        confirmationStatus: subscription.confirmation_status,
        categories: subscription.subscription_categories?.map((sc: any) => ({
          id: sc.category_id,
          name: sc.categories?.name || "Unknown"
        })) || [],
        deliveryTimes: subscription.delivery_times?.map((dt: any) => ({
          id: dt.id,
          time: dt.time_of_day.substring(0, 5),
          categoryId: dt.category_id
        })) || []
      }))
    }));
  } catch (error) {
    console.error("Error in subscriber manager:", error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "Failed to load subscribers"
    });
    return [];
  }
};

export function useSubscriberManager() {
  const queryClient = useQueryClient();

  // Fetch subscribers using useQuery
  const {
    data: subscribers,
    isLoading: isLoadingSubscribers,
    refetch: refetchSubscribers
  } = useQuery({
    queryKey: ["subscribers"],
    queryFn: fetchSubscribers,
    refetchOnWindowFocus: false
  });

  // Cancel subscription mutation
  const cancelSubscription = useMutation({
    mutationFn: async ({ userId, subscriptionId }: { userId: string; subscriptionId: string }) => {
      if (!subscriptionId) throw new Error("No subscription ID provided");
      return await subscriptionService.cancelSubscription(userId, subscriptionId);
    },
    onSuccess: () => {
      toast({ title: "Subscription cancelled", description: "The subscription has been cancelled successfully." });
      queryClient.invalidateQueries({ queryKey: ["subscribers"] });
    },
    onError: (error: any) => {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to cancel subscription" });
    }
  });

  // Delete subscriber mutation
  const deleteSubscriber = useMutation({
    mutationFn: async ({ userId, removeData }: { userId: string; removeData: boolean }) => {
      try {
        // Optionally remove related data
        if (removeData) {
          await supabase.from("message_logs").delete().eq("profile_id", userId);
          await supabase.from("subscription_notifications").delete().eq("subscription_id", userId);
        }
        return await subscriptionService.deleteSubscriber(userId);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      toast({ title: "Subscriber deleted", description: "The subscriber has been removed successfully." });
      queryClient.invalidateQueries({ queryKey: ["subscribers"] });
    },
    onError: (error: any) => {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to delete subscriber" });
    }
  });

  // Update subscriber preferences mutation
  const updateSubscriberPreferences = useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: SubscriptionUpdateData }) => {
      try {
        // Call the service to update the subscription
        const result = await subscriptionService.updateSubscription(userId, data, true);
        return result;
      } catch (error) {
        console.error("Error updating subscription:", error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({ title: "Subscription updated", description: "The subscription preferences have been updated successfully." });
      queryClient.invalidateQueries({ queryKey: ["subscribers"] });
    },
    onError: (error: any) => {
      console.error("Subscription update error full details:", error);
      toast({ 
        variant: "destructive", 
        title: "Error", 
        description: error.message || "Failed to update subscription preferences" 
      });
    }
  });

  // Calculate overall loading state
  const isLoading = isLoadingSubscribers || 
                   cancelSubscription.isPending || 
                   deleteSubscriber.isPending || 
                   updateSubscriberPreferences.isPending;

  return {
    subscribers,
    isLoading,
    refetchSubscribers,
    cancelSubscription,
    deleteSubscriber,
    updateSubscriberPreferences
  };
}
